/**
 * Generic composable for managing playback info fetching and caching
 * Works with any PlaybackInfo type (Video, Audio, Image, Document)
 */

import { ref, type Ref, type ComputedRef } from 'vue'
import { fetchPlaybackInfo, fetchPlaybackInfoBatch } from '@/api/visitor/playback'
import { transformPlaybackInfo, transformBatchPlaybackInfo } from '../../types/transforms'
import type { PlaybackInfo } from '../../types/asset'
import type { ItemToken } from '../../types/embed'

export interface PlaybackInfoCacheEntry<T extends PlaybackInfo = PlaybackInfo> {
  playbackInfo: T
  fetchedAt: number
  expiresAt: number
}

export interface UsePlaybackInfoCacheOptions {
  /** Playback token from embed response (required for authenticated playback) */
  playbackToken?: ComputedRef<string | undefined> | string
  /**
   * Where each item's token comes from, when `playbackToken` reaches only its own asset (the
   * in-app player). Every request is then made with the item's own token, and a batch becomes one
   * request per item.
   */
  itemToken?: ComputedRef<ItemToken | undefined>
  /** Collection playback ID for analytics */
  collectionPlaybackId?: ComputedRef<string | undefined> | string
  /**
   * The session the embed read opened, sent as `session_id` on `playback/info` so the sitting's
   * assets are recorded under it. A prefetch sends none: the batch route records nothing.
   */
  sessionId?: ComputedRef<string | undefined> | string
  /** Seconds before expiry to trigger refresh (default: 60) */
  refreshBeforeExpiry?: number
}

export interface UsePlaybackInfoCacheReturn<T extends PlaybackInfo = PlaybackInfo> {
  /** Cache of playback infos keyed by asset UID */
  cache: Ref<Map<string, PlaybackInfoCacheEntry<T>>>

  /** Set of asset UIDs currently being fetched */
  pendingFetches: Ref<Set<string>>

  /** Get playback info from cache (undefined if not cached or expired) */
  get: (assetUid: string) => T | undefined

  /**
   * Check if playback info is in cache, not expired, and was fetched to play — a prefetched
   * answer is `get`-able but not `has`, so the caller's `fetchOne` records its sitting.
   */
  has: (assetUid: string) => boolean

  /** Check if a fetch is in progress for an asset */
  isFetching: (assetUid: string) => boolean

  /** Fetch playback info for a single asset */
  fetchOne: (assetUid: string) => Promise<T>

  /** Fetch playback info for multiple assets */
  fetchBatch: (assetUids: string[]) => Promise<Map<string, T>>

  /** Manually set playback info in cache */
  set: (assetUid: string, playbackInfo: T) => void

  /** Clear all cached playback info */
  clear: () => void

  /** Get all cached playback infos as array */
  getAll: () => T[]

  /** The token that reaches an asset: the one its playback info was fetched with, else the embed's */
  tokenOf: (assetUid: string | undefined) => string | undefined
}

/**
 * Generic composable for managing playback info fetching and caching.
 * Works with any PlaybackInfo type (VideoPlaybackInfo, AudioPlaybackInfo, etc.)
 *
 * @example
 * ```typescript
 * // For images
 * const cache = usePlaybackInfoCache<ImagePlaybackInfo>()
 *
 * // For audio
 * const cache = usePlaybackInfoCache<AudioPlaybackInfo>()
 * ```
 */
export function usePlaybackInfoCache<T extends PlaybackInfo = PlaybackInfo>(
  options: UsePlaybackInfoCacheOptions = {}
): UsePlaybackInfoCacheReturn<T> {
  const { playbackToken, itemToken, collectionPlaybackId, sessionId, refreshBeforeExpiry = 60 } = options

  const cache = ref<Map<string, PlaybackInfoCacheEntry<T>>>(new Map()) as Ref<
    Map<string, PlaybackInfoCacheEntry<T>>
  >
  const pendingFetches = ref<Set<string>>(new Set())
  // The token each asset's playback info was fetched with. Whatever else is said about that asset —
  // the heartbeat — has to present the same one, because an in-app item's token reaches that item
  // and nothing else.
  const tokens = ref<Map<string, string>>(new Map())
  // The assets whose cached answer came from a prefetch. The batch route opens no analytics
  // session, so such an answer carries no `sessionId`: it is shown at once, and the asset is
  // fetched again through `playback/info` when it is actually played, which records its sitting.
  const prefetched = new Set<string>()

  /**
   * Get the playback token value
   */
  function getPlaybackToken(): string | undefined {
    if (typeof playbackToken === 'string') {
      return playbackToken
    }
    return playbackToken?.value
  }

  /**
   * The token to ask about one asset with: its own when the embed mints one per item, else the
   * embed's. `undefined` for an item the viewer cannot open.
   */
  async function tokenFor(assetUid: string): Promise<string | undefined> {
    const perItem = itemToken?.value
    return perItem ? perItem(assetUid) : getPlaybackToken()
  }

  function tokenOf(assetUid: string | undefined): string | undefined {
    const own = assetUid === undefined ? undefined : tokens.value.get(assetUid)
    return own ?? getPlaybackToken()
  }

  /**
   * The session the sitting is recorded under, when there is one.
   */
  function getSessionId(): string | undefined {
    return typeof sessionId === 'string' ? sessionId : sessionId?.value
  }

  /**
   * Get the collection playback ID value
   */
  function getCollectionPlaybackId(): string | undefined {
    if (typeof collectionPlaybackId === 'string') {
      return collectionPlaybackId
    }
    return collectionPlaybackId?.value
  }

  /**
   * Check if a cache entry is still valid
   */
  function isEntryValid(entry: PlaybackInfoCacheEntry<T>): boolean {
    const now = Date.now()
    const expiresAt = entry.expiresAt - refreshBeforeExpiry * 1000
    return now < expiresAt
  }

  /**
   * Get playback info from cache
   */
  function get(assetUid: string): T | undefined {
    const entry = cache.value.get(assetUid)
    if (!entry) return undefined
    if (!isEntryValid(entry)) {
      cache.value.delete(assetUid)
      return undefined
    }
    return entry.playbackInfo
  }

  /**
   * Check if playback info is in cache, valid, and fetched to be played. A prefetched answer is
   * not: it answers `false`, so the caller's `fetchOne` records the sitting the prefetch did not.
   */
  function has(assetUid: string): boolean {
    return get(assetUid) !== undefined && !prefetched.has(assetUid)
  }

  /**
   * Check if a fetch is in progress
   */
  function isFetching(assetUid: string): boolean {
    return pendingFetches.value.has(assetUid)
  }

  /**
   * Set playback info in cache
   */
  function set(assetUid: string, playbackInfo: T): void {
    prefetched.delete(assetUid)
    const expiresAt = new Date(playbackInfo.expiresAt).getTime()
    cache.value.set(assetUid, {
      playbackInfo,
      fetchedAt: Date.now(),
      expiresAt,
    })
  }

  /**
   * Fetch playback info for a single asset
   */
  async function fetchOne(assetUid: string): Promise<T> {
    // Return cached if available — unless a prefetch cached it, which recorded no sitting
    if (has(assetUid)) return get(assetUid) as T

    // Wait if already fetching
    if (pendingFetches.value.has(assetUid)) {
      // Poll until fetch completes
      await new Promise<void>((resolve) => {
        const checkInterval = setInterval(() => {
          if (!pendingFetches.value.has(assetUid)) {
            clearInterval(checkInterval)
            resolve()
          }
        }, 50)
      })
      if (has(assetUid)) return get(assetUid) as T
      // What was pending was a prefetch; this call is the one that plays it.
      if (!get(assetUid)) throw new Error(`Failed to fetch playback info for ${assetUid}`)
    }

    // Mark as pending
    pendingFetches.value.add(assetUid)

    try {
      const token = await tokenFor(assetUid)
      if (!token) {
        throw new Error(`No playback token reaches ${assetUid}`)
      }

      const rawData = await fetchPlaybackInfo({
        asset_id: assetUid,
        playback_token: token,
        session_id: getSessionId(),
      })

      const playbackInfo = transformPlaybackInfo(rawData) as T
      set(assetUid, playbackInfo)
      tokens.value.set(assetUid, token)
      return playbackInfo
    } finally {
      pendingFetches.value.delete(assetUid)
    }
  }

  /**
   * Fetch playback info for multiple assets
   */
  async function fetchBatch(assetUids: string[]): Promise<Map<string, T>> {
    const result = new Map<string, T>()

    // Filter out already cached, prefetched or fetched to play
    const toFetch = assetUids.filter((uid) => get(uid) === undefined)

    // Add already cached to result
    for (const uid of assetUids) {
      const cached = get(uid)
      if (cached) {
        result.set(uid, cached)
      }
    }

    if (toFetch.length === 0) {
      return result
    }

    // One token per item leaves no one token to batch under, so each item is its own request. One
    // that fails — an item the viewer cannot open among them — is left out of the map, as the batch
    // leaves out an asset its token does not reach.
    if (itemToken?.value) {
      await Promise.allSettled(toFetch.map(async (uid) => result.set(uid, await fetchOne(uid))))
      return result
    }

    // Mark all as pending
    for (const uid of toFetch) {
      pendingFetches.value.add(uid)
    }

    try {
      const token = getPlaybackToken()
      if (!token) {
        throw new Error('Playback token is required')
      }

      const rawData = await fetchPlaybackInfoBatch({
        asset_ids: toFetch,
        playback_token: token,
      })

      const playbackInfos = transformBatchPlaybackInfo(rawData)

      for (const [assetUid, playbackInfo] of playbackInfos) {
        const typedInfo = playbackInfo as T
        set(assetUid, typedInfo)
        prefetched.add(assetUid)
        result.set(assetUid, typedInfo)
      }

      return result
    } finally {
      for (const uid of toFetch) {
        pendingFetches.value.delete(uid)
      }
    }
  }

  /**
   * Clear all cached playback info
   */
  function clear(): void {
    prefetched.clear()
    cache.value.clear()
    tokens.value.clear()
  }

  /**
   * Get all cached playback infos as array
   */
  function getAll(): T[] {
    return Array.from(cache.value.values())
      .filter(isEntryValid)
      .map((entry) => entry.playbackInfo)
  }

  return {
    cache,
    pendingFetches,
    get,
    has,
    isFetching,
    fetchOne,
    fetchBatch,
    set,
    clear,
    getAll,
    tokenOf,
  }
}
